/**
 * Renders `{{Screen name}}` references inside article prose.
 *
 * A reference always renders as a pill, so the reader can tell the difference
 * between a control's name and ordinary prose. Where the name matches a place the
 * app already knows how to point at — the agent's target catalog, which is keyed
 * by translated label — the pill becomes a button that navigates there and
 * flashes the control. Everything else stays a plain pill, which is why an author
 * can reference any screen without waiting for a catalog entry.
 */

import {
  AGENT_TARGETS,
  targetSelector,
} from "@/features/agent-chat/actions/agentTargets"
import { pulseTarget } from "@/features/agent-chat/actions/pulse"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconArrowUpRight } from "@tabler/icons-react"
import { Fragment, useMemo, type ReactNode } from "react"
import { useNavigate } from "react-router"
import { foldForSearch } from "../search"

const REFERENCE = /(\{\{[^}]+\}\})/g
const REFERENCE_ONLY = /^\{\{([^}]+)\}\}$/
/** Long enough for the destination route to mount before we look for the element. */
const PULSE_DELAY_MS = 350

// A key-cap chip, like the shortcut keys in the review studio: quiet enough to
// sit in a sentence, distinct enough to read as "this is the label on screen".
const PILL_CLASS =
  "mx-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-1.5 py-px align-baseline text-[0.875em] font-medium leading-snug text-white"

/** Translated label → target key, so an article can name a control in prose
 * without knowing the catalog's internal keys. */
function useTargetsByLabel(): Map<string, string> {
  const { translate } = useTranslation()
  return useMemo(() => {
    const byLabel = new Map<string, string>()
    for (const [key, target] of Object.entries(AGENT_TARGETS)) {
      const label = target.labelKey ? translate(target.labelKey) : target.label
      if (!label) continue
      // First entry wins: two targets sharing a label would otherwise resolve
      // unpredictably, and the earlier one is the more general surface.
      const folded = foldForSearch(label)
      if (!byLabel.has(folded)) byLabel.set(folded, key)
    }
    return byLabel
  }, [translate])
}

export function ReferencedText({
  text,
  className,
}: {
  text: string
  className?: string
}) {
  const navigate = useNavigate()
  const targetsByLabel = useTargetsByLabel()

  const goToTarget = (targetKey: string) => {
    const target = AGENT_TARGETS[targetKey]
    if (!target) return
    navigate(target.route)
    window.setTimeout(
      () => pulseTarget(targetSelector(targetKey)),
      PULSE_DELAY_MS,
    )
  }

  const nodes: ReactNode[] = text.split(REFERENCE).map((part, index) => {
    const match = part.match(REFERENCE_ONLY)
    if (!match) return <Fragment key={index}>{part}</Fragment>

    const label = match[1]
    const targetKey = targetsByLabel.get(foldForSearch(label))
    if (!targetKey) {
      return (
        <span key={index} className={PILL_CLASS}>
          {label}
        </span>
      )
    }
    // Navigable chips carry an arrow, so a reader can tell which labels take them
    // to the screen before trying.
    return (
      <button
        key={index}
        type="button"
        onClick={() => goToTarget(targetKey)}
        className={cn(
          PILL_CLASS,
          "cursor-pointer transition-colors duration-150 hover:border-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        )}
      >
        {label}
        <IconArrowUpRight
          size={12}
          stroke={2}
          aria-hidden="true"
          className="text-grey5"
        />
      </button>
    )
  })

  return <span className={className}>{nodes}</span>
}
